// client/src/pages/LandingPage.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Navigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';

function QRcreator() {
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();
  
  // QR creator states
  const [name, setName] = useState('');
  const [type, setType] = useState('url');
  const [content, setContent] = useState('');
  const [shape, setShape] = useState('square');
  const [showName, setShowName] = useState(true);
  const [showURL, setShowURL] = useState(false);
  const [foregroundColor, setForegroundColor] = useState('#000000');
  const [backgroundColor, setBackgroundColor] = useState('#FFFFFF');
  const [textColor, setTextColor] = useState('#000000');
  const [logoFile, setLogoFile] = useState(null);
  const [logo, setLogo] = useState(null);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [dragActive, setDragActive] = useState(false);

const handleDrag = (e) => {
  e.preventDefault();
  e.stopPropagation();
  if (e.type === "dragenter" || e.type === "dragover") {
    setDragActive(true);
  } else if (e.type === "dragleave") {
    setDragActive(false);
  }
};

const handleDrop = (e) => {
  e.preventDefault();
  e.stopPropagation();
  setDragActive(false);
  
  if (e.dataTransfer.files && e.dataTransfer.files[0]) {
    // Handle the dropped file(s)
    const file = e.dataTransfer.files[0];
    setLogoFile(file);
    setLogo(URL.createObjectURL(file));
  }
};
  
  // vCard states for contact QR codes
  const [vCardData, setVCardData] = useState({
    name: '',
    phone: '',
    email: '',
    company: ''
  });
  
  // If user is signed in, redirect to dashboard
  if (isSignedIn) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Handler for vCard field changes
  const handleVCardChange = (field, value) => {
    setVCardData({
      ...vCardData,
      [field]: value
    });
  };
  
  // Function to handle QR creation button click
  const handleCreateQR = () => {
    // Save QR code data to localStorage so we can retrieve it after signup
    localStorage.setItem('pendingQR', JSON.stringify({
      name,
      type,
      content,
      shape,
      showName,
      showURL,
      foregroundColor,
      backgroundColor,
      textColor,
      vCardData: type === 'vcard' ? vCardData : undefined
    }));
    
    // Show signup prompt
    setShowSignupPrompt(true);
  };
  
  // Close the signup modal
  const closeSignupPrompt = () => {
    setShowSignupPrompt(false);
  };
  
  // Navigate to sign up page with the saved QR data
  const goToSignup = () => {
    navigate('/sign-up?returnTo=create');
  };
  
  // Function to format vCard data
  const formatVCardData = () => {
    if (type !== 'vcard') return '';
    
    let vcard = 'BEGIN:VCARD\nVERSION:3.0\n';
    
    if (vCardData.name) vcard += `FN:${vCardData.name}\n`;
    if (vCardData.phone) vcard += `TEL:${vCardData.phone}\n`;
    if (vCardData.email) vcard += `EMAIL:${vCardData.email}\n`;
    if (vCardData.company) vcard += `ORG:${vCardData.company}\n`;
    
    vcard += 'END:VCARD';
    
    return vcard;
  };
  
  // Function to format content based on type
  const getFormattedContent = () => {
    if (type === 'vcard') {
      return formatVCardData();
    }
    
    if (!content) return 'https://example.com';
    
    if (type === 'phone') {
      // Format phone with tel: protocol
      const cleanedPhone = content.replace(/(?!^\+)[^\d]/g, '');
      return `tel:${cleanedPhone}`;
    } else if (type === 'url' && !content.startsWith('http://') && !content.startsWith('https://')) {
      return `${content}`; 
    }
    
    return content;
  };
  
  // Handle logo file selection
  const handleLogoChange = (e) => {
    if (e.target.files[0]) {
      setLogoFile(e.target.files[0]);
      setLogo(URL.createObjectURL(e.target.files[0]));
    }
  };
  
  // Label for content type
  const getShowContentLabel = () => {
    switch(type) {
      case 'phone':
        return 'Show Phone Number';
      case 'email':
        return 'Show Email Address';
      case 'text':
        return 'Show Text';
      case 'sms':
        return 'Show SMS Number';
      default:
        return 'Show URL';
    }
  };
  
  return (
    <div className="QRcreator">
      {/* Hero Section */}
      <section className="bg-gray-100 py-10 mt-0 w-screen right-[50%] relative left-[50%] right-[50%] mx-[-50vw]">
        <div className="container max-w-6xl mx-auto px-6">

        </div>
        
        {/* QR Creator */}
        <div className="container max-w-6xl mx-auto px-3 sm:px-0 mb-3">
          {/* Main content area */}
          <h1 className="text-3xl font-bold text-gray-900 md:text-5xl md:font-bold mb-5">
            Create a QR Code
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-8 gap-4">
            {/* Form section */}
            <div className="md:col-span-5 bg-white rounded-lg shadow-sm p-3 md:p-5 mb-6">
              <div>
                <div className="mb-1">
                  <label className="block mt-1 text-m text-gray-700 font-semibold mb-1">Name:</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 font-medium border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="My QR Code"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div className="flex items-center block text-sm text-gray-700 font-medium mb-8">
                  <input
                    type="checkbox"
                    id="showName"
                    className="mr-2"
                    checked={showName}
                    onChange={(e) => setShowName(e.target.checked)}
                  />
                  <label htmlFor="showName">Show Name</label>
                </div>

                {/* QR Code Type Selector Buttons */}
                <div className="mb-4">
                  <label className="block mt-5 text-m text-gray-700 font-semibold mb-3">Select what your QR code will do:</label>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                    <button
                      type="button"
                      className={`px-6 py-4 border-2 rounded-lg font-medium transition duration-200 flex flex-col items-center justify-center ${
                        type === 'url' 
                          ? 'bg-blue-50 border-blue-500 text-blue-600 font-semibold' 
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-700'
                      }`}
                      onClick={() => setType('url')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 015.656 0l4 4a4 4 0 01-5.656 5.656l-1.102-1.101" />
                      </svg>
                      Link to a Website
                    </button>
                    
                    <button
                      type="button"
                      className={`px-6 py-4 border-2 rounded-lg font-medium transition duration-200 flex flex-col items-center justify-center ${
                        type === 'phone' 
                          ? 'bg-blue-50 border-blue-500 text-blue-600 font-semibold' 
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-700'
                      }`}
                      onClick={() => setType('phone')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      Call a Phone Number
                    </button>
                    
                    <button
                      type="button"
                      className={`px-6 py-4 border-2 rounded-lg font-medium transition duration-200 flex flex-col items-center justify-center ${
                        type === 'text' 
                          ? 'bg-blue-50 border-blue-500 text-blue-600 font-semibold' 
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-700'
                      }`}
                      onClick={() => setType('text')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                      </svg>
                      Display Text
                    </button>
                    
                    <button
                      type="button"
                      className={`px-6 py-4 border-2 rounded-lg font-medium transition duration-200 flex flex-col items-center justify-center ${
                        type === 'vcard' 
                          ? 'bg-blue-50 border-blue-500 text-blue-600 font-semibold' 
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-700'
                      }`}
                      onClick={() => setType('vcard')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                      </svg>
                      Create a Contact Card
                    </button>
                  </div>
                  
                  {/* Dynamic input fields based on selected type */}
                  {type === 'vcard' ? (
                    <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <h3 className="text-m text-gray-700 font-semibold ">Contact Information:</h3>
                      
                      <div>
                        <label className="block text-sm text-gray-700 font-medium mb-1">Full Name:</label>
                        <input
                          type="text"
                          className="w-full px-4 py-2 font-medium border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="John Doe"
                          value={vCardData?.name || ''}
                          onChange={(e) => handleVCardChange('name', e.target.value)}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm text-gray-700 font-medium mb-1">Phone:</label>
                        <input
                          type="tel"
                          className="w-full px-4 py-2 font-medium border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="+1 (123) 456-7890"
                          value={vCardData?.phone || ''}
                          onChange={(e) => handleVCardChange('phone', e.target.value)}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm text-gray-700 font-medium mb-1">Email:</label>
                        <input
                          type="email"
                          className="w-full px-4 py-2 font-medium border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="john.doe@example.com"
                          value={vCardData?.email || ''}
                          onChange={(e) => handleVCardChange('email', e.target.value)}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm text-gray-700 font-medium mb-1">Company (optional):</label>
                        <input
                          type="text"
                          className="w-full px-4 py-2 font-medium border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Company Name"
                          value={vCardData?.company || ''}
                          onChange={(e) => handleVCardChange('company', e.target.value)}
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-m text-gray-700 font-semibold mb-1">
                        {type === 'url' ? 'URL' : type === 'phone' ? 'Phone Number' : 'Text'}:
                      </label>
                      <input
                        type={type === 'phone' ? 'tel' : 'text'}
                        className="w-full px-4 py-2 font-medium border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={type === 'url' ? 'www.mywebsite.com' : type === 'phone' ? '+1234567890' : 'Your text here'}
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                      />
                    </div>
                  )}
                </div>

                {/* Only show the URL checkbox for non-vcard types */}
                {type !== 'vcard' && (
                  <div className="flex items-center block text-sm text-gray-700 font-medium mb-8">
                    <input
                      type="checkbox"
                      id="showURL"
                      className="mr-2"
                      checked={showURL}
                      onChange={(e) => setShowURL(e.target.checked)}
                    />
                    <label htmlFor="showURL">{getShowContentLabel()}</label>
                  </div>
                )}

<div className="mb-4">
  <label className="pt-2 block mt-5 text-m text-gray-700 font-semibold mb-2">Add Logo or Image:</label>
  <div 
    className="flex items-center justify-center w-full"
    onDragEnter={handleDrag}
  >
    <label 
      className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors duration-200 ${
        dragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 bg-gray-50 hover:bg-gray-100"
      }`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      <div className="flex flex-col items-center justify-center pt-5 pb-6">
        <svg className={`w-8 h-8 mb-3 ${dragActive ? "text-blue-500" : "text-gray-500"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
        </svg>
        <p className="mb-1 text-sm text-gray-500">
          <span className="font-semibold">Click to upload</span> or drag and drop
        </p>
        <p className="text-xs text-gray-500">SVG, PNG, JPG or GIF</p>
      </div>
      <input 
        type="file" 
        className="hidden"
        accept="image/*"
        onChange={handleLogoChange}
        name="logo"
      />
    </label>
  </div>
  {logo && (
    <div className="mt-3 flex items-center">
      <img src={logo} alt="Logo preview" className="h-8 w-8 object-cover mr-2 rounded" />
      <div className="flex flex-col">
        <span className="text-sm text-gray-600 font-medium">Logo added successfully!</span>
        <button 
          onClick={() => {
            setLogo(null);
            setLogoFile(null);
          }}
          className="text-xs text-red-500 hover:text-red-700"
        >
          Remove
        </button>
      </div>
    </div>
  )}
</div>
              </div>
            </div>

            {/* Preview section */}
            <div className="md:col-span-3 flex flex-col items-center">
              {/* QR Code Preview with styling */}
              <div 
                className={`qr-container p-10 ${shape === 'circle' ? 'rounded-full' : shape === 'rounded' ? 'rounded-2xl' : ''}`}
                style={{ 
                  backgroundColor: backgroundColor || '#f1f1f4',
                  ...(shape === 'circle' ? {
                    width: '310px',  
                    height: '310px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  } : {})
                }}
              >
                <div className="relative">
                  <QRCodeSVG 
                    value={getFormattedContent()} 
                    size={150}
                    fgColor={foregroundColor}
                    bgColor="transparent"
                    level="H"
                  />
                  
                  {/* Logo overlay */}
                  {logo && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      {/* Logo background */}
                      <div 
                        className={`${
                          shape === 'circle' ? 'rounded-full' : 
                          shape === 'rounded' ? 'rounded-lg' : 
                          'rounded-none'
                        }`}
                        style={{
                          width: '60px', 
                          height: '60px', 
                          backgroundColor: backgroundColor,
                          position: 'absolute',
                          zIndex: 5
                        }}
                      />
                      
                      {/* Logo image */}
                      <img 
                        src={logo} 
                        alt="Logo" 
                        style={{
                          width: '40px',
                          height: '40px',
                          zIndex: 10,
                          position: 'absolute'
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Show name if selected */}
                {showName && name && (
                  <div 
                    className="text-center text-lg font-bold mt-3" 
                    style={{ color: textColor || "#000000" }}
                  >
                    {name}
                  </div>
                )}
                
                {/* Show URL if selected */}
                {showURL && content && type !== 'vcard' && (
                  <div 
                    className="text-center text-m font-semibold"
                    style={{ color: textColor || "#000000" }}
                  >
                    {content}
                  </div>
                )}
              </div>
              
              <h2 className="mt-1 text-sm text-gray-400 font-medium mb-6">PREVIEW ONLY</h2>

              {/* Color Customization Section */}

<div className="mb-6 bg-white p-5 rounded-lg shadow-sm">
  <h3 className="block mt-1 text-m text-gray-700 font-semibold mb-2">Customize:</h3>
  
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
    {/* QR Code Color Picker - Extra Large Mobile Tap Area */}
    <div className="space-y-2">
      <label htmlFor="qr-color-picker" className="block text-sm font-medium text-gray-700">QR Code</label>
      <div className="flex items-center space-x-2">
        <div className="relative w-12 h-12">
          {/* The transparent color input with larger tap area */}
          <input
            id="qr-color-picker"
            type="color"
            value={foregroundColor}
            onChange={(e) => setForegroundColor(e.target.value)}
            className="absolute inset-0 w-12 h-12 opacity-0 cursor-pointer z-10"
            style={{ touchAction: "manipulation" }}
          />
          {/* Visual indicator below the input */}
          <div 
            className="absolute inset-0 w-12 h-12 rounded-lg border border-gray-300"
            style={{ backgroundColor: foregroundColor }}
          />
        </div>
        <input
          type="text"
          className="w-full h-12 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
          value={foregroundColor}
          onChange={(e) => setForegroundColor(e.target.value)}
          placeholder="#000000"
        />
      </div>
    </div>
    
    {/* Background Color Picker - Extra Large Mobile Tap Area */}
    <div className="space-y-2">
      <label htmlFor="bg-color-picker" className="block text-sm font-medium text-gray-700">Background</label>
      <div className="flex items-center space-x-2">
        <div className="relative w-12 h-12">
          {/* The transparent color input with larger tap area */}
          <input
            id="bg-color-picker"
            type="color"
            value={backgroundColor}
            onChange={(e) => setBackgroundColor(e.target.value)}
            className="absolute inset-0 w-12 h-12 opacity-0 cursor-pointer z-10"
            style={{ touchAction: "manipulation" }}
          />
          {/* Visual indicator below the input */}
          <div 
            className="absolute inset-0 w-12 h-12 rounded-lg border border-gray-300"
            style={{ backgroundColor: backgroundColor }}
          />
          {backgroundColor.toLowerCase() === '#ffffff' && (
            <div className="absolute inset-0 border border-gray-200 rounded-lg" />
          )}
        </div>
        <input
          type="text"
          className="w-full h-12 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
          value={backgroundColor}
          onChange={(e) => setBackgroundColor(e.target.value)}
          placeholder="#FFFFFF"
        />
      </div>
    </div>
    
    {/* Text Color Picker - Extra Large Mobile Tap Area */}
    <div className="space-y-2">
      <label htmlFor="text-color-picker" className="block text-sm font-medium text-gray-700">Text</label>
      <div className="flex items-center space-x-2">
        <div className="relative w-12 h-12">
          {/* The transparent color input with larger tap area */}
          <input
            id="text-color-picker"
            type="color"
            value={textColor}
            onChange={(e) => setTextColor(e.target.value)}
            className="absolute inset-0 w-12 h-12 opacity-0 cursor-pointer z-10"
            style={{ touchAction: "manipulation" }}
          />
          {/* Visual indicator below the input */}
          <div 
            className="absolute inset-0 w-12 h-12 rounded-lg border border-gray-300"
            style={{ backgroundColor: textColor }}
          />
        </div>
        <input
          type="text"
          className="w-full h-12 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
          value={textColor}
          onChange={(e) => setTextColor(e.target.value)}
          placeholder="#000000"
        />
      </div>
    </div>
  </div>
  
  {/* Color Presets */}
  <div className="mt-5">
    <p className="text-sm text-gray-600 mb-2">Color Presets</p>
    <div className="flex flex-wrap gap-3">
      {/* Make preset buttons larger for mobile */}
      <button 
        className={`h-12 w-12 rounded-lg border-2 flex items-center justify-center overflow-hidden ${
          foregroundColor === '#000000' && backgroundColor === '#FFFFFF' && textColor === '#000000' 
            ? 'border-blue-500' 
            : 'border-gray-300'
        }`}
        onClick={() => {
          setForegroundColor('#000000');
          setBackgroundColor('#FFFFFF');
          setTextColor('#000000');
        }}
        title="Default"
      >
        <div className="w-full h-full bg-white flex items-center justify-center">
          <div className="w-6 h-6 bg-black"></div>
        </div>
      </button>
      
      {/* Blue preset with larger tap area */}
      <button 
        className={`h-12 w-12 rounded-lg border-2 flex items-center justify-center overflow-hidden ${
          foregroundColor === '#1E40AF' && backgroundColor === '#EFF6FF' && textColor === '#1E40AF' 
            ? 'border-blue-500' 
            : 'border-gray-300'
        }`}
        onClick={() => {
          setForegroundColor('#1E40AF');
          setBackgroundColor('#EFF6FF');
          setTextColor('#1E40AF');
        }}
        title="Blue"
      >
        <div className="w-full h-full bg-blue-50 flex items-center justify-center">
          <div className="w-6 h-6 bg-blue-800"></div>
        </div>
      </button>
      
      {/* Additional preset - Green */}
      <button 
        className={`h-12 w-12 rounded-lg border-2 flex items-center justify-center overflow-hidden ${
          foregroundColor === '#047857' && backgroundColor === '#ECFDF5' && textColor === '#047857' 
            ? 'border-blue-500' 
            : 'border-gray-300'
        }`}
        onClick={() => {
          setForegroundColor('#047857');
          setBackgroundColor('#ECFDF5');
          setTextColor('#047857');
        }}
        title="Green"
      >
        <div className="w-full h-full bg-green-50 flex items-center justify-center">
          <div className="w-6 h-6 bg-green-700"></div>
        </div>
      </button>
      
      {/* Additional preset - Red */}
      <button 
        className={`h-12 w-12 rounded-lg border-2 flex items-center justify-center overflow-hidden ${
          foregroundColor === '#DC2626' && backgroundColor === '#FEF2F2' && textColor === '#DC2626' 
            ? 'border-blue-500' 
            : 'border-gray-300'
        }`}
        onClick={() => {
          setForegroundColor('#DC2626');
          setBackgroundColor('#FEF2F2');
          setTextColor('#DC2626');
        }}
        title="Red"
      >
        <div className="w-full h-full bg-red-50 flex items-center justify-center">
          <div className="w-6 h-6 bg-red-600"></div>
        </div>
      </button>
    </div>
    
    {/* Shape Selection - Increase tap areas */}
    <p className="mt-4 text-sm text-gray-600 mb-2">Shape</p>
    <div className="mt-3 grid grid-cols-3 gap-3">
      {/* Square Shape */}
      <button
        type="button"
        className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition duration-200 ${
          shape === 'square' 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
        }`}
        onClick={() => setShape('square')}
      >
        <div className="w-20 h-20 bg-gray-100 flex items-center justify-center mb-3">
          <div className="w-12 h-12 bg-gray-800"></div>
        </div>
        <span className="text-sm font-medium text-gray-800">Square</span>
      </button>
      
      {/* Rounded Shape */}
      <button
        type="button"
        className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition duration-200 ${
          shape === 'rounded' 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
        }`}
        onClick={() => setShape('rounded')}
      >
        <div className="w-20 h-20 bg-gray-100 flex items-center justify-center mb-3">
          <div className="w-12 h-12 bg-gray-800 rounded-lg"></div>
        </div>
        <span className="text-sm font-medium text-gray-800">Rounded</span>
      </button>
      
      {/* Circle Shape */}
      <button
        type="button"
        className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition duration-200 ${
          shape === 'circle' 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
        }`}
        onClick={() => setShape('circle')}
      >
        <div className="w-20 h-20 bg-gray-100 flex items-center justify-center mb-3">
          <div className="w-12 h-12 bg-gray-800 rounded-full"></div>
        </div>
        <span className="text-sm font-medium text-gray-800">Circle</span>
      </button>
    </div>
  </div>
</div>

{/* Shape Customization Section */}


{/* Shape Customization Section */}

            </div>
          </div>
          
          {/* Submit button */}

{/* Submit button - Centered with max width */}
<div className="flex w-full justify-center mt-6 mb-6">
  <div className="w-full max-w-3xl">
    <button
      onClick={handleCreateQR}
      className="w-full px-8 py-4 bg-blue-700 hover:bg-blue-800 text-white font-bold rounded-lg shadow-lg transition duration-200"
    >
      Create My QR Code 
    </button>
  </div>
</div>
        </div>
        
        {/* <div className="container max-w-6xl mx-auto px-6 mb-4">
          <img src="/create.png" alt="BoxyQR Create A QR Code" />
        </div> */}
      </section>

      {/* Signup Prompt Modal */}
      {showSignupPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
            <button 
              onClick={closeSignupPrompt}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="text-center mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-blue-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-bold text-gray-900">Your QR code is ready!</h3>
              <p className="text-gray-600 mt-2">Create a free account to download and use your QR code.</p>
            </div>
            
            <div className="space-y-4">
              <button
                onClick={goToSignup}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition duration-200"
              >
                Create Free Account
              </button>
              
              <button
                onClick={closeSignupPrompt}
                className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-lg transition duration-200"
              >
                Cancel
              </button>
            </div>
            
            <p className="text-xs text-gray-500 text-center mt-6">
              No credit card required. Free plan includes 3 QR codes and tracking features.
            </p>
          </div>
        </div>
      )}

      {/* Features Section */}
      {/* <h2 className="text-4xl font-bold text-center mb-5">Get Started for Free</h2> */}
      {/* <div className="flex justify-center p-3">
        <div className="grid w-full max-w-2xl md:grid-cols-2 gap-4 mb-1 ">
          <Link to="/sign-up" className="flex bg-blue-700 justify-center border-2 border-blue-700 text-white hover:bg-blue-100 hover:text-blue-800 px-10 py-5 rounded-lg font-semibold text-center transition duration-200">
            Create for Free
          </Link>
          
          <Link to="/benefits" className="flex bg-transparent justify-center border-2 text-blue-700 border-blue-700 hover:bg-blue-100 hover:text-indigo-800 px-10 py-5 rounded-lg font-semibold text-center transition duration-200">
            Benefits & Pricing
          </Link>
        </div>
      </div> */}

      <section className="py-16">
        <div className="container max-w-4xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-10 md:mb-0 md:pr-10">
              <h2 className="text-3xl font-bold mb-6">Create & Track Your QR Codes</h2>
              <p className="text-lg text-gray-600 mb-6">
                Our unified platform provides detailed analytics for both QR codes and link tracking in one dashboard, giving you complete visibility into your marketing performance.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Update destinations without reprinting or resharing</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Track both QR scans and link clicks in one place</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>See engagement down to city level</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Monitor by device type</span>
                </li>
              </ul>
            </div>
            <div className="md:w-1/2">
              <div className="">
                <img src="/analytics.png" alt="BoxyQR Analytics Dashboard" className="rounded" 
                     onError={(e) => {e.target.onerror = null; e.target.src = "/analytics.png";}} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container max-w-4xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center">
          <div className="md:w-1/2">
              <div className="">
                <img src="/custom.png" alt="BoxyQR customization options" className="rounded" 
                     onError={(e) => {e.target.onerror = null; e.target.src = "/custom.png";}} />
              </div>
            </div>
            <div className="md:w-1/2 mb-10 md:mb-0 md:pl-10 md:pr-0">
              <h2 className="text-3xl font-bold mb-6">Easy and customizable</h2>
              <p className="text-lg text-gray-600 mb-6">
              Make your QR codes exactly how you want them. Our simple design tools let you create QR codes that look great and still work perfectly.              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Choose colors that match your brand</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Add your own images or logos</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>See how it looks in real-time as you create</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Then easily share and start seeing engagement</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-1 bg-gray-100 w-screen right-[50%] relative left-[50%] right-[50%] mx-[-50vw]">
        <div className="container max-w-4xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8  mb-8 ">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Brand-Matched Designs</h3>
              <p className="text-gray-600">Customize colors, shapes, and add your logo to create tracking assets that complement your brand identity.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Dynamic Destinations</h3>
              <p className="text-gray-600">Change your QR codes and link destinations anytime without reprinting or recreating - update destinations instantly.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Campaign Management</h3>
              <p className="text-gray-600">Organize multiple tracking assets into campaigns and compare performance across marketing initiatives.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50 w-screen right-[50%] relative left-[50%] right-[50%] mx-[-50vw]">
        <div className="container max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12">Data-Driven Tracking in 3 Steps</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl mx-auto">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-full text-white flex items-center justify-center text-3xl font-bold mx-auto mb-4">1</div>
              <h3 className="text-xl font-semibold mb-2">Create Account</h3>
              <p className="text-gray-600">Sign up instantly</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-full text-white flex items-center justify-center text-3xl font-bold mx-auto mb-4">2</div>
              <h3 className="text-xl font-semibold mb-2">Generate Assets</h3>
              <p className="text-gray-600">Custom QRs & trackable links</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-full text-white flex items-center justify-center text-3xl font-bold mx-auto mb-4">3</div>
              <h3 className="text-xl font-semibold mb-2">Deploy & Measure</h3>
              <p className="text-gray-600">See who scans and where</p>
            </div>
          </div>
          <div className="flex justify-center mt-8 p-3">
            <div className="grid w-full max-w-2xl md:grid-cols-1 gap-4 mb-1 ">
              <Link to="/sign-up" className="flex bg-blue-700 justify-center border-2 border-blue-700 text-white hover:bg-blue-100 hover:text-blue-800 px-10 py-5 rounded-lg font-semibold text-center transition duration-200">
                Start Creating Free
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50 w-screen right-[50%] relative left-[50%] right-[50%] mx-[-50vw]">
        <div className="container max-w-5xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12">Other Features</h2>
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-green-500">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold">AI Smart Scanner</h3>
              </div>
              <p className="text-gray-600 mb-4">Scan complex data like product IDs, SNS codes, and more from images. Export to CSV, and create QR codes in bulk.</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <svg className="h-4 w-4 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Auto-detect custom identifiers</span>
                </li>
                <li className="flex items-center">
                  <svg className="h-4 w-4 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>One-click CSV export</span>
                </li>
                <li className="flex items-center">
                  <svg className="h-4 w-4 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Batch QR code generation</span>
                </li>
              </ul>
              <div className="mt-5 pt-4">
                <Link 
                  to={`/AIscanner`} 
                  className="inline-block text-blue-600 hover:text-blue-800 text-m font-semibold"
                >
                  Learn More →
                </Link>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-indigo-500">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 015.656 0l4 4a4 4 0 01-5.656 5.656l-1.102-1.101" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold">Link Tracking</h3>
              </div>
              <p className="text-gray-600 mb-4">Trackable URLs with analytics. Monitor traffic sources and engagement metrics.</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <svg className="h-4 w-4 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Update links without resharing</span>
                </li>
                <li className="flex items-center">
                  <svg className="h-4 w-4 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Track clicks & location data</span>
                </li>
                <li className="flex items-center">
                  <svg className="h-4 w-4 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Campaign performance insights</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-16 w-screen right-[50%] relative left-[50%] right-[50%] mx-[-50vw]">
        <div className="container max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to get started?</h2>
          <p className="text-xl mb-8">Join other data-driven businesses using BoxyQR to customize and track QR codes and links.</p>
        </div>
        <div className="flex justify-center p-3">
          <div className="grid w-full max-w-2xl md:grid-cols-2 gap-4 mb-1 ">
              <Link to="/sign-up" className="flex justify-center bg-white text-blue-700 border-2 border-white hover:bg-blue-200 hover:text-blue-800 px-10 py-5 rounded-lg font-semibold transition text-center duration-200">
                Start Creating Free
              </Link>
              
              <Link to="/benefits" className="flex bg-transparent justify-center border-2 text-white border-white hover:bg-blue-600 hover:text-white px-10 py-5 rounded-lg font-semibold text-center transition duration-200">
                Benefits & Pricing
              </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default QRcreator;